import * as React from 'react';
import { fileUrl } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { TicketAttachmentSummary } from '@ventin/shared';

/**
 * Screenshot attachment gallery (plan §5.5).
 *
 * Thumbnail grid → click opens a lightbox with the full image and the
 * attachment's comment. On the admin surface (`showMetadata`) the lightbox
 * also renders the auto-captured environment (browser/OS/size/console errors);
 * clients see the image + comment only.
 */
export interface AttachmentGalleryProps {
  attachments: TicketAttachmentSummary[];
  /** ADMIN/SUPPORT only — reveal the captured browser/OS/console metadata. */
  showMetadata?: boolean;
}

export function AttachmentGallery({
  attachments,
  showMetadata = false,
}: AttachmentGalleryProps): JSX.Element | null {
  const [activeId, setActiveId] = React.useState<string | null>(null);

  if (attachments.length === 0) return null;

  const active = attachments.find((a) => a.id === activeId) ?? null;

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {attachments.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => setActiveId(a.id)}
            className="group relative overflow-hidden rounded-md border bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={a.comment ? `Screenshot: ${a.comment}` : 'Screenshot'}
          >
            <img
              src={fileUrl(a.imagePath)}
              alt={a.comment ?? 'Ticket screenshot'}
              loading="lazy"
              className="aspect-video w-full object-cover transition-transform group-hover:scale-[1.02]"
            />
            {a.comment && (
              <span className="block truncate px-2 py-1 text-left text-xs text-muted-foreground">
                {a.comment}
              </span>
            )}
          </button>
        ))}
      </div>

      {active && (
        <Lightbox
          attachment={active}
          showMetadata={showMetadata}
          onClose={() => setActiveId(null)}
        />
      )}
    </div>
  );
}

function Lightbox({
  attachment,
  showMetadata,
  onClose,
}: {
  attachment: TicketAttachmentSummary;
  showMetadata: boolean;
  onClose: () => void;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Screenshot preview"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b p-3">
          <h3 className="text-sm font-semibold">Screenshot</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            className="rounded-sm px-2 text-sm text-muted-foreground hover:text-foreground"
          >
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-4">
          <img
            src={fileUrl(attachment.imagePath)}
            alt={attachment.comment ?? 'Ticket screenshot'}
            className="mx-auto max-h-[60vh] max-w-full rounded"
          />
          {attachment.comment && (
            <p className="mt-3 whitespace-pre-wrap text-sm">{attachment.comment}</p>
          )}
          {showMetadata && attachment.meta && <MetadataPanel meta={attachment.meta} />}
        </div>
      </div>
    </div>
  );
}

function MetadataPanel({
  meta,
}: {
  meta: NonNullable<TicketAttachmentSummary['meta']>;
}): JSX.Element {
  const rows: Array<[string, string]> = [];
  if (meta.browser)
    rows.push([
      'Browser',
      `${meta.browser}${meta.browserVersion ? ' ' + meta.browserVersion : ''}`,
    ]);
  if (meta.os) rows.push(['OS', meta.os]);
  if (meta.screenW && meta.screenH) rows.push(['Screen', `${meta.screenW}×${meta.screenH}`]);
  if (meta.viewportW && meta.viewportH)
    rows.push(['Viewport', `${meta.viewportW}×${meta.viewportH}`]);
  if (meta.dpr) rows.push(['DPR', String(meta.dpr)]);
  if (meta.timezone) rows.push(['Timezone', meta.timezone]);
  if (meta.capturedAt) rows.push(['Captured', new Date(meta.capturedAt).toLocaleString()]);

  const errors = meta.consoleErrors ?? [];

  return (
    <section className="mt-4 rounded-md border bg-muted/30 p-3 text-xs">
      <h4 className="mb-2 font-semibold uppercase tracking-wide text-muted-foreground">
        Capture metadata
      </h4>
      {rows.length > 0 ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
          {rows.map(([label, value]) => (
            <div key={label} className="flex flex-col">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="font-medium">{value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-muted-foreground">No environment metadata captured.</p>
      )}

      {errors.length > 0 && (
        <div className="mt-3">
          <h5 className="mb-1 font-semibold text-muted-foreground">Console ({errors.length})</h5>
          <ul className="max-h-40 space-y-1 overflow-auto font-mono">
            {errors.map((e, i) => (
              <li
                key={i}
                className={cn('truncate', e.level === 'warn' ? 'text-amber-700' : 'text-red-700')}
                title={e.message}
              >
                [{e.level}] {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
